import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Input, Spin } from "antd";
import {
  CheckCircleOutlined,
  FrownOutlined,
  LoadingOutlined,
  MehOutlined,
  SmileOutlined,
} from "@ant-design/icons";
import styles from "./styles.module.scss";
import { isHomeHealthExternalUser } from "../../../constants/sso";
import { useVitalsAIMode } from "../../../hooks/useVitalsAIMode";
import { analyticsController, userController } from "../../../AppController";
import { appNotification } from "../../../util/appNotification";
import { AppNotificationType } from "../../../constants/appNotification";
import { shouldShowNasaTlxFromPreference } from "../../../util/nasaTlxPreference";
import {
  NASA_TLX_ITEMS,
  NASA_TLX_PAGE_PAIRS,
  NasaTlxDimensionId,
  NasaTlxItem,
  emptyNasaTlxScores,
} from "./nasaTlxConfig";

const SCALE_POINTS = [1, 2, 3, 4, 5, 6, 7] as const;
const SATISFACTION_MOOD_POINTS = [
  { score: 1, label: "Very dissatisfied" },
  { score: 2, label: "Dissatisfied" },
  { score: 3, label: "Neutral" },
  { score: 4, label: "Satisfied" },
  { score: 5, label: "Very satisfied" },
] as const;
const NASA_TLX_PAGE_COUNT = NASA_TLX_PAGE_PAIRS.length;
const FOLLOW_UP_STEP_INDEX = NASA_TLX_PAGE_COUNT;
const IMPROVEMENT_FEEDBACK_MAX = 5000;

function isFollowUpPromptStatus(
  status: INasaTlxPreferenceResponse["prompt_status"]
): boolean {
  return status === "two_weeks" || status === "thirty_days";
}

/** Mixpanel: log successful submissions only (`stage: submitted` for dashboards). */
const MP_TLX_INITIAL = "Visit Agent NASA TLX Initial Onboarding";
const MP_TLX_REPEATED = "Visit Agent NASA TLX Periodic Feedback";

function renderMoodIcon(score: number) {
  switch (score) {
    case 1:
      return <FrownOutlined className={styles.moodIcon} />;
    case 2:
      return <FrownOutlined className={styles.moodIcon} />;
    case 3:
      return <MehOutlined className={styles.moodIcon} />;
    case 4:
      return <SmileOutlined className={styles.moodIcon} />;
    case 5:
      return <SmileOutlined className={styles.moodIcon} />;
    default:
      return null;
  }
}

function getHashRoute(): string {
  return window.location.hash.replace(/^#/, "").split("?")[0];
}

/** Visit Notes area: list, calendar, note editor, pdf viewer (hash router paths). */
function isVisitNotesHashPath(path: string): boolean {
  return path === "/visit-notes" || path.startsWith("/visit-notes/");
}

function logNasaTlxSubmitted(
  flow: "initial" | "repeated",
  props: Record<string, unknown>
): void {
  analyticsController.logEvent(
    flow === "initial" ? MP_TLX_INITIAL : MP_TLX_REPEATED,
    { stage: "submitted", ...props }
  );
}

function TlxScaleRow({
  item,
  value,
  onChange,
}: {
  item: NasaTlxItem;
  value: number | null;
  onChange: (v: number) => void;
}) {
  return (
    <div className={styles.tlxBlock}>
      <div className={styles.dimensionTitle}>{item.title}</div>
      <div className={styles.questionText}>{item.question}</div>
      <div className={styles.scaleRow}>
        {SCALE_POINTS.map((n) => (
          <div
            key={n}
            role="button"
            tabIndex={0}
            className={`${styles.scaleCell} ${
              value === n ? styles.scaleCellSelected : ""
            }`}
            onClick={() => onChange(n)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onChange(n);
              }
            }}
          >
            <span className={styles.scaleNumber}>{n}</span>
          </div>
        ))}
      </div>
      <div className={styles.scaleLabels}>
        <span>{item.labelLow}</span>
        <span>{item.labelHigh}</span>
      </div>
    </div>
  );
}

function MoodSatisfactionRow({
  title,
  value,
  onChange,
}: {
  title: string;
  value: number | null;
  onChange: (v: number) => void;
}) {
  return (
    <div className={styles.tlxBlock}>
      <div className={styles.followUpQuestion}>{title}</div>
      <div className={styles.moodScaleRow}>
        {SATISFACTION_MOOD_POINTS.map(({ score, label }) => (
          <div
            key={score}
            role="button"
            tabIndex={0}
            aria-label={`${label}, score ${score} of 5`}
            title={`${label} (${score}/5)`}
            className={`${styles.moodScaleButton} ${
              styles[`moodScaleButton${score}`]
            } ${value === score ? styles.moodScaleButtonSelected : ""}`}
            onClick={() => onChange(score)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onChange(score);
              }
            }}
          >
            <span aria-hidden="true">{renderMoodIcon(score)}</span>
          </div>
        ))}
      </div>
      <div className={styles.moodScaleLabels}>
        <span>Not satisfied</span>
        <span>Very satisfied</span>
      </div>
    </div>
  );
}

function allScoresFilled(
  s: Record<NasaTlxDimensionId, number | null>
): s is Record<NasaTlxDimensionId, number> {
  return (
    s.mentalDemand !== null &&
    s.physicalDemand !== null &&
    s.temporalDemand !== null &&
    s.performance !== null &&
    s.effort !== null &&
    s.frustration !== null
  );
}

/**
 * NASA TLX for home health external users. Mandatory full-screen modal when due;
 * preference is fetched only on Visit Notes routes so onboarding/user-info usually runs first.
 * Timing from GET /user/preference/nasa-tlx; POST TLX 1–7 + optional follow-up 1–5 + text.
 */
const HomeHealthNasaTlxPrompt = () => {
  const isVitalsAIMode = useVitalsAIMode();
  const isAuthorized = useSelector((state: any) => state.auth.isAuthorized);
  const user = useSelector((state: any) => state.auth.user);
  const facilityId = useSelector((state: any) => state.clinic.userClinic?.id);
  const facilityName = useSelector(
    (state: any) => state.clinic.userClinic?.name
  );

  const isHomeHealthExternal = useMemo(
    () => isHomeHealthExternalUser(user?.groups, user?.email),
    [user?.groups, user?.email]
  );

  const eligible =
    isAuthorized &&
    isHomeHealthExternal &&
    !isVitalsAIMode &&
    !window.location.href.includes("/#/vitals-ai");

  const [preference, setPreference] =
    useState<INasaTlxPreferenceResponse | null>(null);
  const [preferenceLoading, setPreferenceLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [scores, setScores] = useState(() => emptyNasaTlxScores());
  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [narrativeSatisfaction, setNarrativeSatisfaction] = useState<
    number | null
  >(null);
  const [uxSatisfaction, setUxSatisfaction] = useState<number | null>(null);
  const [improvementFeedback, setImprovementFeedback] = useState("");
  const [hashRoute, setHashRoute] = useState(getHashRoute);
  const [suppressedAfterApiFailure, setSuppressedAfterApiFailure] =
    useState(false);

  const onVisitNotesRoute = isVisitNotesHashPath(hashRoute);

  useEffect(() => {
    const sync = () => {
      setHashRoute((prev) => {
        const next = getHashRoute();
        return next === prev ? prev : next;
      });
    };
    sync();
    window.addEventListener("hashchange", sync);
    window.addEventListener("popstate", sync);
    // createHashRouter navigates via history.pushState/replaceState, which do not
    // fire hashchange (see HTML spec). Poll only while eligible and not yet on the
    // target route; once on visit-notes the prompt is already visible so polling
    // is unnecessary and wastes CPU/battery.
    const intervalId =
      eligible && !onVisitNotesRoute ? window.setInterval(sync, 200) : 0;
    return () => {
      window.removeEventListener("hashchange", sync);
      window.removeEventListener("popstate", sync);
      if (intervalId) window.clearInterval(intervalId);
    };
  }, [eligible, onVisitNotesRoute]);

  const isFollowUpPrompt = preference
    ? isFollowUpPromptStatus(preference.prompt_status)
    : false;
  const totalSteps = isFollowUpPrompt
    ? NASA_TLX_PAGE_COUNT + 1
    : NASA_TLX_PAGE_COUNT;
  const isOnFollowUpStep =
    isFollowUpPrompt && pageIndex === FOLLOW_UP_STEP_INDEX;

  const shouldPrompt = useMemo(() => {
    if (!preference || preferenceLoading || suppressedAfterApiFailure) {
      return false;
    }
    return shouldShowNasaTlxFromPreference(preference);
  }, [preference, preferenceLoading, suppressedAfterApiFailure]);

  useEffect(() => {
    const shouldLockBody =
      eligible && onVisitNotesRoute && (shouldPrompt || showSuccess);
    if (!shouldLockBody) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [eligible, shouldPrompt, showSuccess, onVisitNotesRoute]);

  useEffect(() => {
    if (!eligible || !user?.userID || !onVisitNotesRoute) {
      setPreference(null);
      setPreferenceLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setPreferenceLoading(true);
      try {
        const res = await userController.getNasaTlxPreference();
        if (cancelled) return;
        if (res.success && res.data) {
          setPreference(res.data);
          setSuppressedAfterApiFailure(false);
        } else {
          setPreference(null);
          setSuppressedAfterApiFailure(true);
          appNotification({
            nTitle:
              res.error?.message ??
              "Workload questionnaire unavailable; continuing without it",
            nType: AppNotificationType.ERROR,
          });
        }
      } catch {
        if (cancelled) return;
        setPreference(null);
        setSuppressedAfterApiFailure(true);
        appNotification({
          nTitle: "Workload questionnaire unavailable; continuing without it",
          nType: AppNotificationType.ERROR,
        });
      } finally {
        if (!cancelled) setPreferenceLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [eligible, user?.userID, onVisitNotesRoute]);

  useEffect(() => {
    if (!shouldPrompt) return;
    setPageIndex(0);
    setScores(emptyNasaTlxScores());
    setNarrativeSatisfaction(null);
    setUxSatisfaction(null);
    setImprovementFeedback("");
    setShowSuccess(false);
  }, [shouldPrompt, preference?.next_prompt_at, preference?.prompt_status]);

  const setScore = useCallback((id: NasaTlxDimensionId, v: number) => {
    setScores((prev) => ({ ...prev, [id]: v }));
  }, []);

  const pair =
    pageIndex < NASA_TLX_PAGE_COUNT ? NASA_TLX_PAGE_PAIRS[pageIndex] : null;
  const itemA = pair ? NASA_TLX_ITEMS[pair[0]] : null;
  const itemB = pair ? NASA_TLX_ITEMS[pair[1]] : null;

  const tlxPageComplete =
    !!itemA &&
    !!itemB &&
    scores[itemA.id] !== null &&
    scores[itemB.id] !== null;

  const followUpStepComplete =
    narrativeSatisfaction !== null &&
    uxSatisfaction !== null &&
    improvementFeedback.length <= IMPROVEMENT_FEEDBACK_MAX;

  const onNext = () => {
    if (pageIndex < NASA_TLX_PAGE_COUNT - 1) {
      if (!tlxPageComplete) return;
      setPageIndex((p) => p + 1);
      return;
    }
    if (pageIndex === NASA_TLX_PAGE_COUNT - 1 && isFollowUpPrompt) {
      if (!tlxPageComplete) return;
      setPageIndex(FOLLOW_UP_STEP_INDEX);
    }
  };

  const canGoBack = pageIndex > 0 || isOnFollowUpStep;

  const onBack = () => {
    if (isOnFollowUpStep) {
      setPageIndex(NASA_TLX_PAGE_COUNT - 1);
      return;
    }
    if (pageIndex > 0) {
      setPageIndex((p) => p - 1);
    }
  };

  const syncPreferenceAfterSubmit = async (
    submitBody: INasaTlxSubmitResponseBody
  ) => {
    try {
      const refreshed = await userController.getNasaTlxPreference();
      if (refreshed.success && refreshed.data) {
        setPreference(refreshed.data);
        return;
      }
    } catch {
      // The TLX API must never block access after answers were accepted.
    }
    setPreference({
      has_response: true,
      prompt_status: submitBody.index >= 2 ? "thirty_days" : "two_weeks",
      next_prompt_at: submitBody.next_prompt_at,
      latest_entry: null,
    });
  };

  const onSubmit = async () => {
    if (isSubmitting) return;
    if (isFollowUpPrompt) {
      if (
        !isOnFollowUpStep ||
        !followUpStepComplete ||
        !allScoresFilled(scores)
      ) {
        return;
      }
    } else {
      if (pageIndex !== NASA_TLX_PAGE_COUNT - 1) return;
      if (!tlxPageComplete || !allScoresFilled(scores)) return;
    }

    setIsSubmitting(true);
    try {
      const baseSix = {
        mental_demand: scores.mentalDemand,
        physical_demand: scores.physicalDemand,
        temporal_demand: scores.temporalDemand,
        performance: scores.performance,
        effort: scores.effort,
        frustration: scores.frustration,
      };
      const improvementTrimmed = improvementFeedback
        .trim()
        .slice(0, IMPROVEMENT_FEEDBACK_MAX);
      const answers: INasaTlxAnswersPayload = isOnFollowUpStep
        ? {
            ...baseSix,
            narrative_satisfaction: narrativeSatisfaction!,
            ux_satisfaction: uxSatisfaction!,
            ...(improvementTrimmed.length > 0
              ? { improvement_feedback: improvementTrimmed }
              : {}),
          }
        : baseSix;

      const res = await userController.submitNasaTlxPreference({ answers });
      if (!res.success || !res.data) {
        setSuppressedAfterApiFailure(true);
        appNotification({
          nTitle:
            res.error?.message ??
            "Workload questionnaire unavailable; continuing without it",
          nType: AppNotificationType.ERROR,
        });
        return;
      }
      const submissionIndex = res.data.index;
      const submitFlow: "initial" | "repeated" =
        submissionIndex === 1 ? "initial" : "repeated";
      const mixpanelPayload: Record<string, unknown> = {
        email: user?.email,
        cUserId: user?.userID,
        facilityId,
        facilityName,
        route: getHashRoute(),
        submission_index: submissionIndex,
        next_prompt_at: res.data.next_prompt_at,
        mental_demand: answers.mental_demand,
        physical_demand: answers.physical_demand,
        temporal_demand: answers.temporal_demand,
        performance: answers.performance,
        effort: answers.effort,
        frustration: answers.frustration,
      };
      if (isOnFollowUpStep) {
        mixpanelPayload.narrative_satisfaction = answers.narrative_satisfaction;
        mixpanelPayload.ux_satisfaction = answers.ux_satisfaction;
        mixpanelPayload.has_improvement_feedback =
          improvementTrimmed.length > 0;
        mixpanelPayload.improvement_feedback_length = improvementTrimmed.length;
      }
      logNasaTlxSubmitted(submitFlow, mixpanelPayload);
      await syncPreferenceAfterSubmit(res.data);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setPageIndex(0);
        setScores(emptyNasaTlxScores());
        setNarrativeSatisfaction(null);
        setUxSatisfaction(null);
        setImprovementFeedback("");
      }, 3000);
    } catch {
      setSuppressedAfterApiFailure(true);
      appNotification({
        nTitle: "Workload questionnaire unavailable; continuing without it",
        nType: AppNotificationType.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!eligible) {
    return null;
  }

  if (!onVisitNotesRoute) {
    return null;
  }

  if (preferenceLoading || !preference) {
    return null;
  }

  if (!shouldPrompt && !showSuccess) {
    return null;
  }

  if (showSuccess) {
    return (
      <div
        className={`homeHealthNasaTlxOuterContainer ${styles.modalRoot}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="nasa-tlx-success-title"
      >
        <div className={styles.modalBackdrop} aria-hidden="true" />
        <div className={styles.modalPanel}>
          <div className={styles.successModal} id="nasa-tlx-success-title">
            <div className={styles.successIcon}>
              <CheckCircleOutlined />
            </div>
            <div className={styles.successTitle}>Thank you</div>
            <div className={styles.successSubtitle}>
              Your workload responses help us improve the product.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isLastTlxPage = pageIndex === NASA_TLX_PAGE_COUNT - 1;

  return (
    <div
      className={`homeHealthNasaTlxOuterContainer ${styles.modalRoot}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="nasa-tlx-dialog-title"
    >
      <div className={styles.modalBackdrop} aria-hidden="true" />
      <div className={styles.modalPanel}>
        <Spin
          spinning={isSubmitting}
          indicator={
            <LoadingOutlined style={{ fontSize: 48, color: "#161646" }} spin />
          }
        >
          <div className={styles.surveyContainer}>
            <div className={styles.headerTitle} id="nasa-tlx-dialog-title">
              {isOnFollowUpStep ? "Circadia feedback" : "Task Load Assessment"}
            </div>
            <div className={styles.headerSubtitle}>
              {isOnFollowUpStep ? (
                "Help us understand how Circadia is working for you."
              ) : preference.prompt_status === "initial" ? (
                <>
                  <strong>Prior to using Circadia App</strong>, in your current
                  day-to-day
                </>
              ) : (
                <>
                  Please rate your experience on each scale
                  <strong> using the Circadia app.</strong>
                </>
              )}
            </div>
            <div className={styles.stepHint}>
              Step {pageIndex + 1} of {totalSteps}
            </div>

            {isOnFollowUpStep ? (
              <>
                <MoodSatisfactionRow
                  title="How satisfied are you with the content of the Narrative?"
                  value={narrativeSatisfaction}
                  onChange={setNarrativeSatisfaction}
                />
                <MoodSatisfactionRow
                  title="How satisfied are you with the user experience?"
                  value={uxSatisfaction}
                  onChange={setUxSatisfaction}
                />
                <div className={styles.tlxBlock}>
                  <div className={styles.followUpQuestion}>
                    How might we improve our product to better meet your needs?
                  </div>
                  <Input.TextArea
                    className={styles.feedbackTextArea}
                    value={improvementFeedback}
                    onChange={(e) => setImprovementFeedback(e.target.value)}
                    placeholder="Share any ideas or feedback (optional)"
                    maxLength={IMPROVEMENT_FEEDBACK_MAX}
                    showCount
                    rows={5}
                    aria-label="Improvement feedback"
                  />
                </div>
              </>
            ) : (
              <>
                {itemA && (
                  <TlxScaleRow
                    item={itemA}
                    value={scores[itemA.id]}
                    onChange={(v) => setScore(itemA.id, v)}
                  />
                )}
                {itemB && (
                  <TlxScaleRow
                    item={itemB}
                    value={scores[itemB.id]}
                    onChange={(v) => setScore(itemB.id, v)}
                  />
                )}
              </>
            )}

            <div className={styles.buttonContainer}>
              {canGoBack && (
                <Button
                  type="default"
                  size="large"
                  htmlType="button"
                  disabled={isSubmitting}
                  onClick={onBack}
                >
                  Back
                </Button>
              )}
              {isOnFollowUpStep ? (
                <Button
                  type="primary"
                  className="circadiaButton"
                  size="large"
                  htmlType="button"
                  disabled={!followUpStepComplete || isSubmitting}
                  onClick={onSubmit}
                >
                  Submit
                </Button>
              ) : isLastTlxPage && !isFollowUpPrompt ? (
                <Button
                  type="primary"
                  className="circadiaButton"
                  size="large"
                  htmlType="button"
                  disabled={!tlxPageComplete || isSubmitting}
                  onClick={onSubmit}
                >
                  Submit
                </Button>
              ) : (
                <Button
                  type="primary"
                  className="circadiaButton"
                  size="large"
                  htmlType="button"
                  disabled={!tlxPageComplete || isSubmitting}
                  onClick={onNext}
                >
                  Next
                </Button>
              )}
            </div>
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default HomeHealthNasaTlxPrompt;
